import type { ApexChartEventOpts, ApexOptions } from 'apexcharts';
import { TFunction } from 'i18next';
import { getWeek, getYear, getMonth, getQuarter } from 'date-fns';
import { PeriodGroupByEnum } from 'graphql-common';
import { getApexchartsLocales } from '@lib/utils/apexcharts';
import { TrendBucket } from './fillTrendGaps';
import { TREND_QUARTER, TrendGroupBy } from './getTrendGroupByOptions';

// i18n keys for month abbreviations, indexed 0–11 (January–December).
const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

/**
 * Formats the representative date of a bucket into a human-readable x-axis
 * label for the given granularity.
 *
 * Year is intentionally omitted from Day / Week / Month / Quarter labels
 * because the chart always renders a single month or year at a time, making
 * the year redundant.
 */
function formatBucketLabel(
  date: Date,
  groupBy: TrendGroupBy,
  t: TFunction<'translation', undefined>,
): string {
  switch (groupBy) {
    case PeriodGroupByEnum.Year:
      return String(getYear(date));
    case TREND_QUARTER:
      return `Q${getQuarter(date)}`;
    case PeriodGroupByEnum.Month:
      return t(MONTH_KEYS[getMonth(date)]);
    case PeriodGroupByEnum.Week:
      return `W${getWeek(date, { weekStartsOn: 1 })}`;
    case PeriodGroupByEnum.Day:
    default:
      return String(date.getDate()).padStart(2, '0');
  }
}

interface BuildArgs {
  buckets: TrendBucket[];
  groupBy: TrendGroupBy;
  locale: string;
  t: TFunction<'translation', undefined>;
}

/**
 * Converts a list of filled trend buckets into ApexCharts `options` and
 * `series` ready to pass directly to the `<Chart>` component.
 *
 * Series layout (stacked bar):
 *   index 0 — Unique views (green, bottom)
 *   index 1 — Repeat views = allViews − uniqueViews (blue, top)
 *
 * The legend and tooltip use `inverseOrder: true` so "All views" appears
 * first (the total is more prominent than the breakdown).
 *
 * A categorical x-axis is used instead of datetime/numeric so bars stay
 * exactly aligned with their labels through zoom / pan / reset — datetime
 * axes recompute tick positions on zoom, which visually drifts bars off
 * their labels.
 *
 * Hover crosshair: implemented via `addXaxisAnnotation` on `dataPointMouseEnter`
 * so the dotted line is centered on the bar column (xaxis.crosshairs cannot
 * reliably center on a category-axis bar).
 */
export default function buildTrendChartData({
  buckets,
  groupBy,
  locale,
  t,
}: BuildArgs): { options: ApexOptions; series: ApexOptions['series'] } {
  const categories = buckets.map((b) => formatBucketLabel(b.date, groupBy, t));

  const series = [
    {
      name: t('unique-views'),
      data: buckets.map((b) => b.uniqueViews),
    },
    {
      // Show only the increment above unique views so stacking reads as a total
      name: t('all-views'),
      data: buckets.map((b) => Math.max(0, b.allViews - b.uniqueViews)),
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      ...getApexchartsLocales(locale),
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true,
        allowMouseWheelZoom: false,
      },
      events: {
        dataPointMouseEnter: (
          _event: MouseEvent,
          chartContext?: ApexCharts,
          config?: ApexChartEventOpts,
        ) => {
          if (!chartContext || !config) return;
          chartContext.clearAnnotations();
          const label = categories[config.dataPointIndex];
          if (!label) return;
          chartContext.addXaxisAnnotation({
            x: label,
            strokeDashArray: 4,
            borderColor: '#b6b6b6',
            borderWidth: 1,
            label: { text: '' },
          });
        },
        dataPointMouseLeave: (
          _event: MouseEvent,
          chartContext?: ApexCharts,
        ) => {
          chartContext?.clearAnnotations();
        },
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '50%',
        borderRadius: 2,
      },
    },
    dataLabels: { enabled: false },
    // Series order: index 0 = unique (green, bottom), index 1 = all-views remainder (blue, top)
    colors: ['#1AAB6F', '#324DDA'],
    states: {
      hover: {
        // `value` is a blend fraction towards black (0 = no change, 1 = black).
        // Keep it low for a subtle hover tint.
        filter: { type: 'darken', value: 0.1 },
      },
      active: {
        // Prevent non-hovered bars from dimming on click (default stacked-bar behaviour)
        filter: { type: 'none' },
      },
    },
    xaxis: {
      type: 'category',
      categories,
      tooltip: { enabled: false },
      labels: {
        style: { fontSize: '12px' },
        rotate: 0,
        hideOverlappingLabels: true,
      },
    },
    yaxis: {
      title: { text: t('view-count') },
      forceNiceScale: true,
      decimalsInFloat: 0,
      min: 0,
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      // Show "All views" first in the legend (opposite of stacking order)
      inverseOrder: true,
      markers: {
        shape: 'circle',
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: false,
      // Mirror legend order: "All views" first, "Unique views" second
      inverseOrder: true,
    },
    grid: { strokeDashArray: 3 },
  };

  return { options, series };
}
